/**
 * Ledger fixed-asset endpoints (bos-api).
 *
 * Paths are relative to the axios client's base, which already ends in `/api/v1`
 * — so `/ledger/fixed-assets` reaches `…/api/v1/ledger/fixed-assets`. This
 * mirrors `AccountsEndpoints`, `JournalEndpoints`, `StatementsEndpoints`,
 * `VatEndpoints` and `PeriodsEndpoints`, the other live ledger resources.
 *
 * What the client's own contract assumed and got wrong, all measured against the
 * deployed spec on 2026-08-02:
 *
 * - **The prefix is `/ledger/fixed-assets`**, not `/fixed-assets`.
 * - **Editing is `PUT /{id}`**, not `PATCH`.
 * - **Disposal is `/{id}/disposal/preview` and `/{id}/disposal`**, not
 *   `/{id}/disposal/prepare` and `/{id}/disposal/commit`.
 * - **The run commits to `/depreciation-runs`**, not `/depreciation-runs/commit`.
 * - **`/{id}/acquisition/commit` does not exist.** The server links the entry to
 *   its asset when the entry posts; there is no client-side confirmation step,
 *   and so no route for one.
 * - **`GET /reconciliation` is new**, beyond the contract entirely.
 *
 * Two query notes, both measured:
 *
 * - **`Status` and `Search` are capitalized on the wire** and bind
 *   case-insensitively — but there is no reason to rely on that, so the spec's
 *   spelling is what goes out. An unrecognised `Status` value is refused with
 *   ASP.NET ProblemDetails rather than an `ApiError`.
 * - **The register takes no `locale`.** It was accepted and never honoured, and
 *   has been removed from the spec; names arrive as `{ en, ka }` maps. Only
 *   `/reconciliation` — whose `accountName` *is* resolved server-side — takes one.
 */

const search = (entries: Record<string, string>): string => {
    const query = new URLSearchParams(entries).toString();
    return query ? `?${query}` : '';
};

export type FixedAssetsListQuery = {
    /** `ALL` | `ACTIVE` | `DISPOSED`. */
    status: string;
    /** Free text; omitted from the URL when blank. */
    search: string;
};

export const FixedAssetsEndpoints = {
    /** GET — the seeded asset categories with their three-account mapping. */
    categories: () => '/ledger/fixed-assets/categories',

    /** GET — the register. Summary totals are ACTIVE-only whatever the filter. */
    list: ({ status, search: term }: FixedAssetsListQuery) =>
        `/ledger/fixed-assets${search({
            Status: status,
            ...(term ? { Search: term } : {}),
        })}`,

    /** GET — one asset with its full schedule and the **posted** entries on it. */
    detail: (assetId: string) => `/ledger/fixed-assets/${assetId}`,

    /**
     * POST — create the asset. Builds a DRAFT acquisition entry, or nothing at
     * all when `isOpening` is set.
     */
    create: () => '/ledger/fixed-assets',

    /** PUT — edit an asset's descriptive and depreciation fields. */
    update: (assetId: string) => `/ledger/fixed-assets/${assetId}`,

    /** POST — compute the gain/loss and build a draft. Writes nothing. */
    previewDisposal: (assetId: string) =>
        `/ledger/fixed-assets/${assetId}/disposal/preview`,

    /**
     * POST — record the disposal terms and persist the DRAFT entry.
     *
     * The asset stays `ACTIVE`: its position moves when the entry posts.
     */
    commitDisposal: (assetId: string) =>
        `/ledger/fixed-assets/${assetId}/disposal`,

    /** POST — per-asset charges + a balanced draft for a period. No write. */
    previewRun: () => '/ledger/fixed-assets/depreciation-runs/preview',

    /**
     * POST — persist the run and its DRAFT entry. Idempotent per period, guarded
     * by a server-side unique index.
     */
    commitRun: () => '/ledger/fixed-assets/depreciation-runs',

    /**
     * GET — the tie-out, both sides computed server-side.
     *
     * `locale` resolves `accountName`; it defaults to `ka` and falls back to `ka`
     * on an unknown value, so it is always sent explicitly.
     */
    reconciliation: (locale: string) =>
        `/ledger/fixed-assets/reconciliation${search({ locale })}`,
};
